import { createAxiosInstance, getAuthToken } from "@/dataProvider/axios";
import env from "@/config/env";
import { useAuthStoreV1 } from "@/stores/authStore_v1";
import { useShallow } from "zustand/react/shallow";
import { getDefaultOrgId } from "@/utils/defaultOrg";
import type {
  McpConnector,
  McpConnectorPreset,
  McpGatewayTool,
  McpToolInvokeResult,
  CreateMcpConnectorInput,
  StartMcpOAuthInput,
} from "./types";
import {
  MCP_PRESETS_ENDPOINT,
  MCP_CONNECTORS_ENDPOINT,
  MCP_TOOLS_ENDPOINT,
  MCP_OAUTH_START_ENDPOINT,
  MCP_CONNECTOR_DISCOVER_ENDPOINT,
  MCP_CONNECTOR_DELETE_ENDPOINT,
  MCP_TOOL_INVOKE_ENDPOINT,
} from "./queries";

export * from "./types";
export * from "./queries";

const cleanBaseUrl = (value: string): string => value.replace(/\/+$/, "");
const proxyBaseUrl = cleanBaseUrl(env.API_URL);

export const MCP_GATEWAY_API_BASE_URL = cleanBaseUrl(
  env.MCP_GATEWAY_API_URL || `${proxyBaseUrl}/proxy/mcp-gateway`
);

export const mcpGatewayAxios = createAxiosInstance(MCP_GATEWAY_API_BASE_URL);

export function useMcpGatewayRequestContext(): { orgId: string | null; userId: string | null } {
  const { selectedOrgId, organizations, userId } = useAuthStoreV1(
    useShallow((s) => ({
      selectedOrgId: s.selectedOrg?.id || null,
      organizations: s.organizations,
      userId: s.user?.id || null,
    }))
  );
  return {
    orgId:
      selectedOrgId ||
      getDefaultOrgId() ||
      readBrowserOrgId() ||
      (organizations.length === 1 ? organizations[0].id : null),
    userId,
  };
}

export function mcpGatewayHeaders(orgId?: string | null): Record<string, string> {
  const state = useAuthStoreV1.getState();
  const effectiveOrgId = orgId || resolveCurrentOrgId(state);
  const headers: Record<string, string> = {};
  if (effectiveOrgId) {
    headers["X-Organization-ID"] = effectiveOrgId;
  }
  const token = getAuthToken();
  const userId = state.user?.id || readUserIdFromJwt(token);
  if (userId) headers["X-User-ID"] = userId;
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

export function toolsSurfaceHeaders(orgId?: string | null): Record<string, string> {
  return { ...mcpGatewayHeaders(orgId), "X-NCoder-Surface": "tools" };
}

function resolveCurrentOrgId(state: ReturnType<typeof useAuthStoreV1.getState>): string | null {
  if (state.selectedOrg?.id) return state.selectedOrg.id;
  const defaultOrgId = getDefaultOrgId();
  if (defaultOrgId) return defaultOrgId;
  const legacyOrgId = readBrowserOrgId();
  if (legacyOrgId) return legacyOrgId;
  return state.organizations.length === 1 ? state.organizations[0].id : null;
}

function readBrowserOrgId(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const stored = window.localStorage.getItem("org_id");
    if (stored) return stored;
    const cookie = document.cookie
      .split(";")
      .map((part) => part.trim())
      .find((part) => part.startsWith("org_id="));
    return cookie ? decodeURIComponent(cookie.slice("org_id=".length)) : null;
  } catch {
    return null;
  }
}

function readUserIdFromJwt(token: string | null): string | null {
  if (!token) return null;
  try {
    const [, payload] = token.split(".");
    if (!payload) return null;
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), "=");
    const decoded = JSON.parse(atob(padded)) as Record<string, unknown>;
    const userId = decoded.user_id || decoded.userId || decoded.sub || decoded.id;
    return typeof userId === "string" && userId.trim() ? userId.trim() : null;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// API functions — called only from hooks/tools/
// ---------------------------------------------------------------------------

export async function listMcpConnectorPresets(orgId?: string | null): Promise<McpConnectorPreset[]> {
  const response = await mcpGatewayAxios.get<{ presets: McpConnectorPreset[] }>(MCP_PRESETS_ENDPOINT, {
    headers: mcpGatewayHeaders(orgId),
  });
  return response.data.presets || [];
}

export async function listMcpConnectors(orgId?: string | null): Promise<McpConnector[]> {
  const response = await mcpGatewayAxios.get<{ connectors: McpConnector[] }>(MCP_CONNECTORS_ENDPOINT, {
    headers: mcpGatewayHeaders(orgId),
  });
  return response.data.connectors || [];
}

export async function createMcpConnector(
  input: CreateMcpConnectorInput,
  orgId?: string | null,
): Promise<McpConnector> {
  const response = await mcpGatewayAxios.post<{ connector: McpConnector }>(MCP_CONNECTORS_ENDPOINT, input, {
    headers: toolsSurfaceHeaders(orgId),
  });
  return response.data.connector;
}

export async function startMcpOAuth(input: StartMcpOAuthInput, orgId?: string | null): Promise<string> {
  const response = await mcpGatewayAxios.post<{ authorize_url: string }>(
    MCP_OAUTH_START_ENDPOINT(input.provider),
    { name: input.name },
    { headers: toolsSurfaceHeaders(orgId) },
  );
  return response.data.authorize_url;
}

export async function deleteMcpConnector(connectorId: string, orgId?: string | null): Promise<void> {
  await mcpGatewayAxios.delete(MCP_CONNECTOR_DELETE_ENDPOINT(connectorId), {
    headers: mcpGatewayHeaders(orgId),
  });
}

export async function discoverMcpConnector(
  connectorId: string,
  orgId?: string | null,
): Promise<{ connector: McpConnector; tools: McpGatewayTool[] }> {
  const response = await mcpGatewayAxios.post<{ connector: McpConnector; tools: McpGatewayTool[] }>(
    MCP_CONNECTOR_DISCOVER_ENDPOINT(connectorId),
    {},
    { headers: mcpGatewayHeaders(orgId) },
  );
  return { connector: response.data.connector, tools: response.data.tools || [] };
}

export async function listMcpGatewayTools(orgId?: string | null): Promise<McpGatewayTool[]> {
  const response = await mcpGatewayAxios.get<{ tools: McpGatewayTool[] }>(MCP_TOOLS_ENDPOINT, {
    headers: mcpGatewayHeaders(orgId),
  });
  return response.data.tools || [];
}

export async function invokeMcpGatewayTool(
  toolId: string,
  args: Record<string, unknown> = {},
  orgId?: string | null,
): Promise<McpToolInvokeResult> {
  const response = await mcpGatewayAxios.post<McpToolInvokeResult>(
    MCP_TOOL_INVOKE_ENDPOINT(toolId),
    { arguments: args },
    { headers: toolsSurfaceHeaders(orgId) },
  );
  return response.data;
}
